import React, { useState } from 'react';
import { Box, Typography, Avatar, IconButton, Menu, MenuItem, Chip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import ActivityReactions from '../common/ActivityReactions';
import { addPostReaction, removePostReaction, deletePost } from '../../services/api';

export interface PostData {
  id: number;
  user_id: number;
  username: string;
  display_name: string;
  avatar: string | null;
  game: {
    id: number;
    name: string;
    cover_image: string | null;
  } | null;
  content: string;
  reaction_counts: { [key: number]: number } | null;
  reply_count: number;
  visibility: number;
  created_at: string;
  updated_at: string;
  user_reaction: number | null;
}

interface PostCardProps {
  post: PostData;
  currentUserId?: number | null;
  isMyPage?: boolean;
  isProfile?: boolean;
  onDelete?: (postId: number) => void;
  onReactionUpdate?: (postId: number, newReactions: { [key: number]: number }, newUserReaction: number | null) => void;
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);

  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分前`;
  if (hours < 24) return `${hours}時間前`;
  if (days < 30) return `${days}日前`;
  if (months < 12) return `${months}ヶ月前`;
  return `${Math.floor(days / 365)}年前`;
};

const getCoverUrl = (coverId: string | null) => {
  if (!coverId) return '/image/no_image.png';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

const PostCard: React.FC<PostCardProps> = ({ post, currentUserId, isMyPage, isProfile, onDelete, onReactionUpdate }) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isOwner = isMyPage ?? (currentUserId === post.user_id);
  const coverUrl = isProfile && post.game?.cover_image ? getCoverUrl(post.game.cover_image) : null;

  const handleDelete = async () => {
    setMenuAnchor(null);
    try {
      await deletePost(post.id);
      onDelete?.(post.id);
    } catch (error) {
      console.error('ポストの削除に失敗しました:', error);
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2 },
        pt: { xs: 1, sm: 2 },
        pb: { xs: 0.5, sm: 1 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.02)' },
        transition: 'background-color 0.15s',
      }}
    >
      {/* ヘッダー: アバター + ユーザー情報 + 時間 + メニュー */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 } }}>
        <Avatar
          src={coverUrl || post.avatar || undefined}
          variant={coverUrl ? 'rounded' : 'circular'}
          sx={{
            width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 },
            cursor: 'pointer',
            bgcolor: 'grey.800', fontSize: '0.9rem',
            ...(coverUrl && { borderRadius: 1 }),
          }}
          onClick={() => coverUrl ? navigate(`/game/${post.game!.id}`) : navigate(`/profile/${post.username}`)}
        >
          {(post.display_name || post.username)?.[0]?.toUpperCase()}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* ユーザー名 + 時間 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontWeight: 600, cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                fontSize: '0.75rem',
                lineHeight: 1.2,
                flexShrink: 1,
                minWidth: 0,
              }}
              onClick={() => navigate(`/profile/${post.username}`)}
            >
              {post.display_name || post.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem', lineHeight: 1, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
              @{post.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem', lineHeight: 1, flexShrink: 0 }}>
              · {formatTimeAgo(post.created_at)}
            </Typography>
            {post.visibility === 1 && (
              <VolumeOffIcon sx={{ fontSize: '0.85rem', color: 'text.secondary', flexShrink: 0 }} />
            )}
            {post.visibility === 2 && (
              <VisibilityOffIcon sx={{ fontSize: '0.85rem', color: 'text.secondary', flexShrink: 0 }} />
            )}

            {/* メニュー（自分のポストのみ） */}
            {isOwner && (
              <IconButton
                size="small"
                sx={{ ml: 'auto', p: 0.5, flexShrink: 0 }}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <MoreHorizIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
          </Box>

          {/* コンテンツ */}
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.35,
              mb: post.game ? 1 : 0,
            }}
          >
            {post.content}
          </Typography>

          {/* ゲームタグ */}
          {post.game && (
            <Chip
              icon={<SportsEsportsIcon sx={{ fontSize: '0.85rem !important' }} />}
              label={post.game.name}
              size="small"
              onClick={() => navigate(`/game/${post.game!.id}`)}
              sx={{
                height: { xs: 20, sm: 24 },
                color: 'text.secondary',
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                cursor: 'pointer',
                mb: 0.5,
              }}
            />
          )}

          {/* リアクション */}
          <ActivityReactions
            targetId={post.id}
            reactions={post.reaction_counts || {}}
            userReaction={post.user_reaction}
            isPublic={!isOwner}
            addReaction={addPostReaction}
            removeReaction={removePostReaction}
            onReactionUpdate={onReactionUpdate}
          />
        </Box>
      </Box>

      {/* 削除メニュー */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main', fontSize: '0.85rem' }}>
          削除
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PostCard;
